import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Percent, 
  Clock, 
  AlertTriangle,
  Users,
  MessageCircle,
  Bell,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle2
} from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, LineChart, Line, Area, AreaChart } from 'recharts';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

export default function CollectionDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/collection/dashboard');
      setData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar dashboard de cobrança');
    } finally {
      setLoading(false);
    }
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${months[parseInt(month) - 1]}/${year.slice(2)}`;
  };

  const getRiskBadge = (risk) => {
    switch(risk) {
      case 'alto':
        return <span className="inline-flex px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">Alto</span>;
      case 'medio':
        return <span className="inline-flex px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">Médio</span>;
      default:
        return <span className="inline-flex px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">Baixo</span>;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>Erro ao carregar dados do dashboard.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const { summary, comparison, overdue_distribution, monthly_evolution, top_defaulters } = data;

  return (
    <div className="p-6 space-y-6" data-testid="collection-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Dashboard de Cobrança</h1>
          <p className="text-muted-foreground mt-1">Análise completa da inadimplência e recuperação</p>
        </div>
        <div className="flex gap-2">
          <Link to="/communication">
            <Button variant="outline">
              <MessageCircle className="w-4 h-4 mr-2" />
              Cobrar
            </Button>
          </Link>
          <Link to="/reminders">
            <Button>
              <Bell className="w-4 h-4 mr-2" />
              Lembretes
            </Button>
          </Link>
        </div>
      </div>

      {/* KPIs Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-border/60 border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total em Atraso</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.total_overdue_amount)}</p>
                <p className="text-xs text-muted-foreground mt-1">{summary.total_overdue_titles} títulos</p>
              </div>
              <div className="p-2 bg-red-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60 border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Taxa de Inadimplência</p>
                <p className="text-2xl font-bold text-orange-600">{summary.delinquency_rate}%</p>
                <p className="text-xs text-muted-foreground mt-1">dos títulos emitidos</p>
              </div>
              <div className="p-2 bg-orange-100 rounded-lg">
                <Percent className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60 border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Taxa de Recuperação</p>
                <p className="text-2xl font-bold text-green-600">{summary.recovery_rate}%</p>
                <p className="text-xs text-muted-foreground mt-1">pagos após vencimento</p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60 border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tempo Médio de Atraso</p>
                <p className="text-2xl font-bold text-blue-600">{summary.avg_days_overdue} dias</p>
                <p className="text-xs text-muted-foreground mt-1">média geral</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Comparativo Mensal */}
      <Card className="border-border/60">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Mês Atual ({formatMonth(comparison.current_month)})</p>
                <p className="text-xl font-bold">{formatCurrency(comparison.current_month_overdue)}</p>
              </div>
              <div className="text-2xl text-muted-foreground">vs</div>
              <div>
                <p className="text-sm text-muted-foreground">Mês Anterior ({formatMonth(comparison.prev_month)})</p>
                <p className="text-xl font-bold">{formatCurrency(comparison.prev_month_overdue)}</p>
              </div>
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              comparison.variation_percent <= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {comparison.variation_percent <= 0 ? (
                <ArrowDownRight className="w-5 h-5" />
              ) : (
                <ArrowUpRight className="w-5 h-5" />
              )}
              <span className="text-lg font-bold">{Math.abs(comparison.variation_percent)}%</span>
              <span className="text-sm">{comparison.variation_percent <= 0 ? 'redução' : 'aumento'}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Evolução Mensal */}
        <Card className="border-border/60">
          <CardHeader>
            <CardTitle className="text-lg">Evolução da Inadimplência</CardTitle>
            <CardDescription>Últimos 12 meses</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthly_evolution.map(d => ({
                  ...d,
                  month: formatMonth(d.month)
                }))}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `R$ ${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="overdue" 
                    name="Em Atraso" 
                    stroke="#ef4444" 
                    fill="#fecaca" 
                    strokeWidth={2}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="recovered" 
                    name="Recuperado" 
                    stroke="#10b981" 
                    fill="#bbf7d0" 
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Distribuição por Faixa */}
        <Card className="border-border/60">
          <CardHeader>
            <CardTitle className="text-lg">Distribuição por Faixa de Atraso</CardTitle>
            <CardDescription>Valores por período de atraso</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={overdue_distribution.filter(d => d.value > 0)}
                    dataKey="value"
                    nameKey="range"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ range, percent }) => `${range}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {overdue_distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              {overdue_distribution.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <span className="text-sm">{item.range}: {formatCurrency(item.value)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Inadimplentes */}
      <Card className="border-border/60">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Principais Inadimplentes</CardTitle>
              <CardDescription>Famílias com maior valor em atraso</CardDescription>
            </div>
            <Link to="/communication">
              <Button variant="outline" size="sm">
                Ver Todos
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {top_defaulters.length === 0 ? (
            <Alert className="border-green-500 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Parabéns! Não há inadimplentes no momento.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Responsável</TableHead>
                    <TableHead>Aluno(s)</TableHead>
                    <TableHead className="text-center">Títulos</TableHead>
                    <TableHead className="text-center">Dias Atraso</TableHead>
                    <TableHead className="text-center">Risco</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-24">Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {top_defaulters.slice(0, 10).map((defaulter, index) => (
                    <TableRow key={defaulter.responsible_id} data-testid={`defaulter-${index}`}>
                      <TableCell className="font-medium">{defaulter.responsible_name}</TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground">
                          {defaulter.student_names.slice(0, 2).join(', ')}
                          {defaulter.student_names.length > 2 && ` +${defaulter.student_names.length - 2}`}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                          {defaulter.titles_count}
                        </span>
                      </TableCell>
                      <TableCell className="text-center font-mono">
                        {defaulter.oldest_days}
                      </TableCell>
                      <TableCell className="text-center">
                        {getRiskBadge(defaulter.risk_level)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-red-600">
                        {formatCurrency(defaulter.total_overdue)}
                      </TableCell>
                      <TableCell>
                        {defaulter.whatsapp && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => {
                              const phone = defaulter.whatsapp.replace(/\D/g, '');
                              const fullPhone = phone.length <= 11 ? `55${phone}` : phone;
                              window.open(`https://wa.me/${fullPhone}`, '_blank');
                            }}
                          >
                            <MessageCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resumo de Valores */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border/60 bg-red-50/50">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-500" />
            <p className="text-sm text-muted-foreground">Em Atraso</p>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.total_overdue_amount)}</p>
          </CardContent>
        </Card>
        
        <Card className="border-border/60 bg-yellow-50/50">
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
            <p className="text-sm text-muted-foreground">A Vencer</p>
            <p className="text-2xl font-bold text-yellow-600">{formatCurrency(summary.total_pending_amount)}</p>
          </CardContent>
        </Card>
        
        <Card className="border-border/60 bg-green-50/50">
          <CardContent className="p-4 text-center">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
            <p className="text-sm text-muted-foreground">Recebido</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.total_paid_amount)}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
